import { clamp } from '@calm/evexia-web-common';
import {
    MediaEl,
    PlayerMediaType,
    PlayerWithTracks,
} from '@rhg/components/media/types';
import { AudioEvent, MediaEvent, VideoEvent } from '@rhg/utils/media';
import { useCallback, useEffect, useRef } from 'react';
import { MediaTrackerFn } from './types';

const trackedVideoEvents = [
    VideoEvent.Play,
    VideoEvent.Pause,
    VideoEvent.Ended,
    VideoEvent.Seeked,
    VideoEvent.LoadedMetadata,
];

const trackedAudioEvents = [
    AudioEvent.Play,
    AudioEvent.Pause,
    AudioEvent.Ended,
    AudioEvent.Seeked,
    AudioEvent.LoadedMetadata,
];

function coerceNaNToUndefined(value: number | undefined): number | undefined {
    if (typeof value === 'undefined') {
        return value;
    }
    return isNaN(value) ? undefined : value;
}

interface MediaTrackerProps {
    assetType: PlayerMediaType;
    /** An additional boolean if you want to specify exactly when the tracker should be readied */
    isReady?: boolean;
    willTrackProgress?: boolean;
    mediaEl?: MediaEl;
    trackFn?: MediaTrackerFn;
}
export function useMediaTracker({
    assetType,
    mediaEl,
    trackFn,
    willTrackProgress = false,
    isReady = true,
}: MediaTrackerProps) {
    const isTrackerSet = useRef(false);

    const trackProgress = useCallback(
        (e: Event) => {
            // Duration comes in as NaN if the media element is not ready
            const duration = coerceNaNToUndefined(
                assetType === 'video'
                    ? (mediaEl as PlayerWithTracks)?.duration()
                    : (mediaEl as HTMLAudioElement)?.duration
            );

            const currentTime = clamp(
                0,
                // currentTime comes in as NaN if the media element is not ready
                coerceNaNToUndefined(
                    assetType === 'video'
                        ? (mediaEl as PlayerWithTracks)?.currentTime()
                        : (mediaEl as HTMLAudioElement)?.currentTime
                ) ?? 0,
                duration ?? 0
            );
            // We're not finished if the media element is not ready
            const isFinished =
                typeof duration !== 'undefined' &&
                duration - 1 - currentTime <= 0;

            if (trackFn && e) {
                const eventType = e.type as MediaEvent;
                const isPaused =
                    eventType === VideoEvent.Pause ||
                    eventType === AudioEvent.Pause;
                const isSeeked =
                    eventType === VideoEvent.Seeked ||
                    eventType === AudioEvent.Seeked;

                if (isFinished && (isPaused || isSeeked)) {
                    // The pause event is fired when the audio/video is finished. This becomes "noise" in the logs since we already have the "ended" event.
                    // The seek event can be fired along with the completion if they scrub to the end of the video/audio.
                    return;
                }

                trackFn({
                    assetType,
                    eventType,
                    currentTime,
                    duration,
                    isFinished,
                });
            }
        },
        [assetType, mediaEl, trackFn]
    );

    const attachEventListeners = useCallback(() => {
        isTrackerSet.current = true;
        if (assetType === 'video') {
            (mediaEl as PlayerWithTracks).on(
                [
                    ...trackedVideoEvents,
                    ...(willTrackProgress ? [VideoEvent.TimeUpdate] : []),
                ],
                trackProgress
            );
        } else {
            [
                ...trackedAudioEvents,
                ...(willTrackProgress ? [AudioEvent.TimeUpdate] : []),
            ].forEach((eventType) =>
                (mediaEl as HTMLAudioElement).addEventListener(
                    eventType,
                    trackProgress
                )
            );
        }
    }, [assetType, mediaEl, willTrackProgress, trackProgress]);

    const detachEventListeners = useCallback(() => {
        isTrackerSet.current = false;
        if (assetType === 'video') {
            (mediaEl as PlayerWithTracks).off(
                [
                    ...trackedVideoEvents,
                    ...(willTrackProgress ? [VideoEvent.TimeUpdate] : []),
                ],
                trackProgress
            );
        } else {
            [
                ...trackedAudioEvents,
                ...(willTrackProgress ? [AudioEvent.TimeUpdate] : []),
            ].forEach((eventType) =>
                (mediaEl as HTMLAudioElement).removeEventListener(
                    eventType,
                    trackProgress
                )
            );
        }
    }, [assetType, mediaEl, willTrackProgress, trackProgress]);

    // Attach listeners if ready and detach on unmount
    useEffect(() => {
        if (trackFn && !isTrackerSet.current && !!mediaEl && isReady) {
            attachEventListeners();
        }

        return () => {
            if (mediaEl) {
                detachEventListeners();
            } else {
                isTrackerSet.current = false;
            }
        };
    }, [detachEventListeners, attachEventListeners, isReady, mediaEl, trackFn]);
}
